import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import Table from "components/Table";
import { fetchAllContracts } from "store/appSlice";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectContracts, selectMe } from "store/selectors";
import { ProtectedPaths } from "routes";
import FilterComponent from "components/FilterComponent/FilterComponent";
import { AttachFile } from "@mui/icons-material";
import { fetchExportContracts } from "api/resources/contracts";
import downloadCSV from "helpers/excelExport";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  adminContractColumns,
  adminContractColumnsPlaceholders,
  userContractColumns,
  userContractColumnsPlaceholers,
} from "../contractColumns";
import { getClientInfoFromRoles } from "helpers/clientInfoFromRoles";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

const ContractsTable = () => {
  const dispatch = useAppDispatch();
  const contracts = useAppSelector(selectContracts);
  const me = useAppSelector(selectMe);
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filters from URL params on mount & on URL change
  const getFiltersFromSearchParams = (): { [key: string]: string } => {
    const filters: { [key: string]: string } = {};
    searchParams.forEach((value, key) => {
      filters[key] = value;
    });
    return filters;
  };

  const [filters, setFilters] = useState<{ [key: string]: string }>(
    getFiltersFromSearchParams(),
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortParam, setSortParam] = useState<string | undefined>(undefined);

  const allFilters = sortParam ? { ...filters, sort: sortParam } : filters;

  // Fetch data when filters, page, or sort changes
  useEffect(() => {
    dispatch(fetchAllContracts(currentPage, 25, allFilters));
  }, [currentPage, filters, sortParam, dispatch]);

  // Sync filters with URL if filters change
  useEffect(() => {
    // Remove empty values to avoid cluttering URL
    const cleanedFilters: { [key: string]: string } = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v.trim() !== "") cleanedFilters[k] = v;
    });

    setSearchParams(cleanedFilters);
  }, [filters, setSearchParams]);

  // When URL changes externally (e.g., browser back/forward), update filters state
  useEffect(() => {
    setFilters(getFiltersFromSearchParams());
    setCurrentPage(0);
  }, [searchParams]);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortParam(`${field},${direction}`);
  };

  const handleFilter = (newFilters: { [key: string]: string }) => {
    if (me && getClientInfoFromRoles(me.roles)) {
      const clientInfo = getClientInfoFromRoles(me.roles);
      if (clientInfo && !clientInfo.isAdmin) {
        newFilters.client = clientInfo.clientName;
      }
    }
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const columns = [
    {
      accessorKey: "meteringPoint",
      header: "Metering point",
      cell: (row: any) =>
        `${row.row.original.meteringPoint.address} ${row.row.original.meteringPoint.city} (${row.row.original.meteringPoint.eic})`,
    },
    {
      accessorKey: "client",
      header: "Client",
      cell: (row: any) => (
        <Link to={`/${ProtectedPaths.Clients}/${row.row.original.client.id}`}>
          {`${row.row.original.client.name} (${row.row.original.client.eic})`}
        </Link>
      ),
    },
    {
      accessorKey: "firstDate",
      header: "Beginning of contract",
      cell: (row: any) =>
        row.row.original.firstDate
          ? dayjs(row.row.original.firstDate).format("DD.MM.YYYY")
          : "",
    },
    {
      accessorKey: "lastDate",
      header: "End of contract",
      cell: (row: any) =>
        row.row.original.lastDate
          ? dayjs(row.row.original.lastDate).format("DD.MM.YYYY")
          : "",
    },
    {
      accessorKey: "settings",
      header: "",
    },
  ];

  const isAdmin = useMemo(() => {
    if (!me) return false;
    return getClientInfoFromRoles(me.roles).isAdmin;
  }, [me]);

  const filterColumns = useMemo(() => {
    return isAdmin ? adminContractColumns : userContractColumns;
  }, [isAdmin]);

  const filterColumnPlaceholders = useMemo(() => {
    return isAdmin
      ? adminContractColumnsPlaceholders
      : userContractColumnsPlaceholers;
  }, [isAdmin]);

  const handleExportContracts = () => {
    setLoading(true);
    fetchExportContracts(allFilters)
      .then((response) => downloadCSV(response, `Contracts`))
      .catch((error) => console.error("Error exporting contracts", error))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Contracts</Typography>
        <LoadingButton
          loading={loading}
          variant="contained"
          size="small"
          startIcon={<AttachFile />}
          onClick={handleExportContracts}
        >
          Export contracts
        </LoadingButton>
      </Box>

      <FilterComponent
        onFilter={handleFilter}
        columns={filterColumns}
        columnPlaceholders={filterColumnPlaceholders}
        filters={filters}
      />

      <Table
        data={contracts?.content}
        columns={columns}
        isFetching={!contracts}
        pageCount={contracts?.totalPages}
        page={setCurrentPage}
        searchLabel="Search for client"
        onSort={handleSort}
      />
    </>
  );
};

export default ContractsTable;
